import { BaseFieldRenderer } from './base-field-renderer';
import { DropdownFieldJsonData, FieldOption, RenderOptions } from '../../models/element-types';
import { TranslateUtils } from '../../utils/translate.utils';
import { DomUtils } from '../../utils/dom-utils';
import { HtmlUtils } from '../../utils/html.utils';

export class DropdownFieldRenderer extends BaseFieldRenderer<DropdownFieldJsonData> {
    protected override get defaultData(): DropdownFieldJsonData {
        const option = TranslateUtils.DROPDOWN_FIELD.OPTION;
        return {
            id: '',
            type: 'dropdown',
            label: TranslateUtils.DROPDOWN_FIELD.LABEL,
            placeholder: TranslateUtils.DROPDOWN_FIELD.PLACEHOLDER,
            required: false,
            hiddenLabel: false,
            options: [
                { value: `${option} 1`, label: `${option} 1` },
                { value: `${option} 2`, label: `${option} 2` },
                { value: `${option} 3`, label: `${option} 3` }
            ]
        };
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected override renderContent(fieldJsonData: DropdownFieldJsonData, options: RenderOptions = {}): HTMLElement {
        const elementContent = document.createElement('div');
        elementContent.classList.add('element-content');

        const fieldContainer = document.createElement('div');
        fieldContainer.classList.add('field');
        if (fieldJsonData.required) {
            fieldContainer.classList.add('required');
        }

        if (!fieldJsonData.hiddenLabel) {
            const label = this.renderLabel(fieldJsonData);
            fieldContainer.appendChild(label);
        }

        const dropdown = document.createElement('div');
        dropdown.classList.add('dropdown');
        dropdown.setAttribute('tabindex', '0');

        const input = document.createElement('input');
        input.id = `field-${fieldJsonData.id}`;
        input.classList.add('dropdown-input', 'input', 'default');
        input.type = 'text';
        input.readOnly = true;
        input.setAttribute('tabindex', '-1');
        input.name = fieldJsonData.id;

        if (fieldJsonData.showPlaceholder && fieldJsonData.placeholder) {
            input.placeholder = fieldJsonData.placeholder || '';
        }

        if (fieldJsonData.required) {
            input.required = true;
        }

        const plainLabel = this.getPlainLabel(fieldJsonData.label);
        if (plainLabel) {
            input.dataset['label'] = plainLabel;
        }

        dropdown.appendChild(input);

        const dropdownIcon = document.createElement('div');
        dropdownIcon.classList.add('dropdown-icon');

        const arrowButton = document.createElement('button');
        arrowButton.classList.add('dropdown-arrow-icon');
        arrowButton.type = 'button';
        arrowButton.setAttribute('tabindex', '-1');

        const iconSpan = document.createElement('span');
        iconSpan.classList.add('icon', 'icon-arrow-down');

        arrowButton.appendChild(iconSpan);
        dropdownIcon.appendChild(arrowButton);
        dropdown.appendChild(dropdownIcon);

        const optionsContainer = document.createElement('div');
        optionsContainer.classList.add('options');

        const fieldOptions = fieldJsonData.options || [];
        fieldOptions.forEach(option => {
            const optionElement = this.createOption(option, fieldJsonData.id);
            optionsContainer.appendChild(optionElement);
        });

        dropdown.appendChild(optionsContainer);
        fieldContainer.appendChild(dropdown);

        const errorContainer = this.renderErrorContainer(fieldJsonData);
        fieldContainer.appendChild(errorContainer);

        if (fieldJsonData.showDescription) {
            const description = this.renderDescription(fieldJsonData);
            fieldContainer.appendChild(description);
        }

        elementContent.appendChild(fieldContainer);

        return elementContent;
    }

    private createOption(option: FieldOption, parentId: string): HTMLElement {
        const optionElement = document.createElement('div');
        optionElement.classList.add('option');
        optionElement.id = DomUtils.generateUniqueElementId([]);
        optionElement.setAttribute('name', `dropdown_${parentId}`);
        optionElement.setAttribute('tabindex', '0');
        optionElement.innerHTML = HtmlUtils.sanitize(option.label);

        return optionElement;
    }

    protected renderInput(): HTMLElement {
        return document.createElement('div');
    }
}
